import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'warning' | 'danger';
}

export const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Discard',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'warning',
}: ConfirmDialogProps) => {
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Dialog */}
      <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6">
          {/* Icon */}
          <div
            className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
              variant === 'danger' ? 'bg-[var(--color-error-100)]' : 'bg-yellow-100'
            }`}
          >
            <AlertTriangle
              className={`h-6 w-6 ${variant === 'danger' ? 'text-[var(--color-error-600)]' : 'text-yellow-600'}`}
            />
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-center text-[var(--color-neutral-900)] mb-2">{title}</h3>
          <p className="text-sm text-center text-[var(--color-neutral-600)] mb-6">{message}</p>

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button variant="outline" onClick={onCancel} className="w-full py-3 text-base">
              {cancelLabel}
            </Button>
            <Button
              variant={variant === 'danger' ? 'destructive' : 'default'}
              onClick={onConfirm}
              className={`w-full py-3 text-base ${
                variant === 'warning' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''
              }`}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
